import React, { createContext, useContext, useState, useEffect } from "react";
import i18n from "i18next";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";

const PREFERRED_LANGUAGE_KEY = "user.preferredLanguage";

type Language = "en" | "ar";

const DEFAULT_LANGUAGE: Language = "ar";

interface LanguageContextType {
	language: Language;
	setLanguage: (language: Language) => void;
	isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
	const context = useContext(LanguageContext);
	if (context === undefined) {
		throw new Error("useLanguage must be used within a LanguageProvider");
	}
	return context;
};

interface LanguageProviderProps {
	children: React.ReactNode;
}

function isValidLanguage(lang: string): lang is Language {
	return lang === "ar" || lang === "en";
}

function getStoredLanguage(): Language | null {
	try {
		const stored = localStorage.getItem(PREFERRED_LANGUAGE_KEY);
		if (stored && isValidLanguage(stored)) return stored;
	} catch {
		// ignore
	}
	return null;
}

function setStoredLanguage(lang: Language): void {
	try {
		localStorage.setItem(PREFERRED_LANGUAGE_KEY, lang);
	} catch {
		// ignore
	}
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
	const { isAuthenticated } = useAuth();
	const { data: profile } = useCompanyProfile({ enabled: isAuthenticated });

	// Initialize: user preference (localStorage) first, then company profile, then default
	const [language, setLanguageState] = useState<Language>(() => {
		const stored = getStoredLanguage();
		if (stored) return stored;
		return DEFAULT_LANGUAGE;
	});

	// Sync from company profile only when we have no stored user preference (e.g. first visit)
	useEffect(() => {
		if (!isAuthenticated) return;
		const stored = getStoredLanguage();
		if (stored) return; // user already has a preference, don't overwrite
		if (profile?.language && isValidLanguage(profile.language)) {
			setLanguageState(profile.language);
		}
	}, [isAuthenticated, profile?.language]);

	// Apply language to document and i18n
	const isRTL = language === "ar";
	useEffect(() => {
		i18n.changeLanguage(language);
		document.documentElement.dir = isRTL ? "rtl" : "ltr";
		document.documentElement.lang = language;
		document.documentElement.classList.remove("font-arabic", "font-english");
		document.documentElement.classList.add(isRTL ? "font-arabic" : "font-english");
	}, [language, isRTL]);

	const setLanguage = (newLanguage: Language) => {
		setLanguageState(newLanguage);
		setStoredLanguage(newLanguage);
	};

	const value: LanguageContextType = {
		language,
		setLanguage,
		isRTL,
	};

	return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
